import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import MessageActionsPanel from "../MessageActionsPanel";
import MessageReactions from "../MessageReactions";
import "./index.scss";
import MessageThreadBar from "../MessageThreadBar";
import MessageStatus from "../../atoms/MessageStatus";
import useLocaleDateFormatter from "../../../hooks/useLocaleDateFormatter";

export default function MessageRowCompact({
  messageId,
  activeMessageId,
  setActiveMessageId,
  timestamp,
  status,
  children,
  onReact,
  onMore,
  quickEmojis,
  canReact,
  reactions,
  threadReplyCount,
  onOpenThread,
}) {
  const { formatTime } = useLocaleDateFormatter();

  const timestampDate = useMemo(() => {
    if (!timestamp) {
      return null;
    }
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [timestamp]);

  const renderedTime = timestampDate ? formatTime(timestampDate) : "";
  const timeDateTimeValue = timestampDate ? timestampDate.toISOString() : undefined;

  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const isActive = isEmojiPickerOpen || activeMessageId === messageId;

  const handleQuickReact = useCallback((emoji) => onReact?.(emoji, { remove: false, source: "quick" }), [onReact]);
  const handleMore = useCallback((event) => onMore?.(event), [onMore]);
  const handleToggleReaction = useCallback(
    (emoji, reacted) => onReact?.(emoji, { remove: reacted, source: "chip" }),
    [onReact]
  );

  return (
    <div
      className="message-row-compact"
      role="listitem"
      tabIndex={0}
      aria-label="Message"
      onMouseEnter={() => setActiveMessageId?.(messageId)}
      onFocus={() => setActiveMessageId?.(messageId)}
      onMouseLeave={() => { if (!isEmojiPickerOpen) setActiveMessageId?.(null); }}
      onBlur={() => { if (!isEmojiPickerOpen) setActiveMessageId?.(null); }}
    >
      <time className="message-row-compact__time" aria-hidden={true} dateTime={timeDateTimeValue}>{renderedTime}</time>
      <div className="message-row-compact__content">
        {children}
        {Number.isFinite(threadReplyCount) && threadReplyCount > 0 && (
          <MessageThreadBar
            count={threadReplyCount}
            onOpen={onOpenThread}
          />
        )}
        {Array.isArray(reactions) && reactions.length > 0 && (
          <MessageReactions
            reactions={reactions}
            onToggleReaction={handleToggleReaction}
            canReact={canReact}
          />
        )}
        {status ? (
          <div className="message-row-compact__status">
            <MessageStatus status={status} />
          </div>
        ) : null}
      </div>
      {isActive && (
        <MessageActionsPanel
          className="message-row-compact__actions"
          quickEmojis={quickEmojis}
          onQuickReact={handleQuickReact}
          onEmojiPickerOpenChange={(open) => setIsEmojiPickerOpen(Boolean(open))}
          onMore={handleMore}
        />
      )}
    </div>
  );
}

MessageRowCompact.propTypes = {
  messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setActiveMessageId: PropTypes.func,
  timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  status: PropTypes.string,
  children: PropTypes.node,
  onReact: PropTypes.func,
  onMore: PropTypes.func,
  quickEmojis: PropTypes.arrayOf(PropTypes.string),
  canReact: PropTypes.bool,
  reactions: PropTypes.arrayOf(PropTypes.shape({
    emoji: PropTypes.string.isRequired,
    count: PropTypes.number,
    reacted: PropTypes.bool,
  })),
  threadReplyCount: PropTypes.number,
  onOpenThread: PropTypes.func,
};

MessageRowCompact.defaultProps = {
  activeMessageId: null,
  setActiveMessageId: undefined,
  timestamp: undefined,
  status: null,
  children: null,
  onReact: undefined,
  onMore: undefined,
  quickEmojis: ["✅", "👀", "🙌"],
  canReact: true,
  reactions: [],
  threadReplyCount: 0,
  onOpenThread: undefined,
};
